import React, { useState, useContext, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, Modal, Platform, ScrollView } from 'react-native';
import { useGoogleLogin } from '@react-oauth/google';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import { FamilyUser } from '../../interface/User';
import DataOps from '../../data/DataOps';
import SignUpWizard from './components/setupwizard/SignUpWizard';
import { loginWithFacebook } from '../../utility/facebookAuth';
import { identifyUser, sanitizeTelemetryError, trackEvent } from '../../utility/telemetry';

const COUNTRIES = [
	{ name: 'United States', code: '+1', flag: '🇺🇸' },
	{ name: 'Canada', code: '+1', flag: '🇨🇦' },
];

interface LogInModalProps {
	visible: boolean;
	setVisible: (visible: boolean) => void;
	onContinue: (phoneRaw: string) => void;
	isLoading?: boolean;
	phoneError?: string;
}

const LogInModal: React.FC<LogInModalProps> = ({ visible, setVisible, onContinue, isLoading = false, phoneError = '' }) => {
	const [phoneNumber, setPhoneNumber] = useState('');
	const [countryCode, setCountryCode] = useState('United States (+1)');
	const [isCountryDropdownVisible, setIsCountryDropdownVisible] = useState(false);
	const [hoveredButton, setHoveredButton] = useState<string | null>(null);
	const [showAccountTypeSetup, setShowAccountTypeSetup] = useState(false);
	const [newUserData, setNewUserData] = useState<any>(null);
	const [isSocialLoading, setIsSocialLoading] = useState(false);
	const { setUser, setLikedCamps } = useContext(GlobalStateContext);
	const isAnyLoginLoading = isLoading || isSocialLoading;
	const hasTrackedModalViewRef = useRef(false);

	useEffect(() => {
		if (visible && !hasTrackedModalViewRef.current) {
			trackEvent('auth_modal_viewed', {
				surface: 'login_modal',
			});
			hasTrackedModalViewRef.current = true;
		}

		if (!visible) {
			hasTrackedModalViewRef.current = false;
		}
	}, [visible]);

	useEffect(() => {
		if (!visible || !phoneError) {
			return;
		}

		trackEvent('phone_verification_failed', {
			surface: 'login_modal',
			error_message: phoneError.slice(0, 180),
		});
	}, [phoneError, visible]);

	const handleClose = () => {
		trackEvent('auth_modal_closed', {
			surface: 'login_modal',
		});
		setVisible(false);
		setPhoneNumber('');
	};

	const handleSetupClose = () => {
		setShowAccountTypeSetup(false);
		setNewUserData(null);
		setVisible(false);
	};

	const handleGoogleAuthSuccess = async (tokenResponse: any) => {
		if (Platform.OS !== 'web') return;
		setIsSocialLoading(true);
		try {
			const accessToken = tokenResponse?.access_token;
			if (!accessToken) {
				throw new Error('Error getting auth token from Google.');
			}

			const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			});
			const userInfo = await response.json();
			const normalizedEmail = (userInfo?.email || '').trim().toLowerCase();

			// Preferred ID: Firebase uid (same as mobile). Fallback to legacy Google profile id.
			let authId = userInfo?.id;
			try {
				const firebaseCredential = GoogleAuthProvider.credential(undefined, accessToken);
				const userCredential = await signInWithCredential(DataOps.auth, firebaseCredential);
				if (userCredential?.user?.uid) {
					authId = userCredential.user.uid;
				}
			} catch (firebaseSignInError) {
				console.warn('Google token could not be exchanged for Firebase UID, using legacy Google ID fallback.', firebaseSignInError);
			}

			if (!authId) {
				throw new Error('Unable to identify Google account.');
			}

			let userResult = await DataOps.fetchUserFromAnyCollection(authId);
			if (!userResult && normalizedEmail) {
				userResult = await DataOps.fetchUserByEmailFromAnyCollection(normalizedEmail);
				if (userResult?.user?.userId && userResult.user.authId !== authId) {
					await DataOps.saveCustomDataToFirebase(
						userResult.collection,
						userResult.user.userId,
						{ authId },
						true
					);
					userResult = {
						...userResult,
						user: { ...userResult.user, authId },
					};
				}
			}

			if (userResult) {
				const existingUser = userResult.user;
				await DataOps.saveDataToStorage('user', existingUser);
				setLikedCamps(new Set(existingUser.likedCamps || []));
				setUser(existingUser);
				identifyUser(existingUser.userId, {
					account_type: existingUser.accountType || 'unknown',
					auth_provider: 'google',
				});
				trackEvent('auth_existing_user_authenticated', {
					auth_provider: 'google',
					account_type: existingUser.accountType || 'unknown',
				});
				setVisible(false);
				return;
			}

			setNewUserData({
				email: normalizedEmail,
				firstName: userInfo?.given_name || '',
				lastName: userInfo?.family_name || '',
				userName: userInfo?.name || `${userInfo?.given_name || ''} ${userInfo?.family_name || ''}`.trim(),
				firebaseUid: authId,
			});
			trackEvent('auth_new_user_setup_required', {
				auth_provider: 'google',
			});
			setShowAccountTypeSetup(true);
		} catch (error) {
			console.error('Error during Google Sign-In:', error);
			trackEvent('auth_attempt_failed', {
				auth_provider: 'google',
				...sanitizeTelemetryError(error, 'google_sign_in_failed'),
			});
			alert(error instanceof Error ? error.message : 'Google sign-in failed. Please try again.');
		} finally {
			setIsSocialLoading(false);
		}
	};

	const googleLogin = useGoogleLogin({
		onSuccess: handleGoogleAuthSuccess,
		onError: () => {
			setIsSocialLoading(false);
			trackEvent('auth_attempt_failed', {
				auth_provider: 'google',
				error_name: 'google_oauth_error',
			});
			alert('Google sign-in failed. Please try again.');
		},
	});

	const handleFacebookLogin = async () => {
		trackEvent('auth_method_clicked', {
			auth_provider: 'facebook',
			surface: 'login_modal',
		});
		setIsSocialLoading(true);
		try {
			const result = await loginWithFacebook();
			if (!result?.isCancelled && result?.accessToken) {
				trackEvent('facebook_access_token_received', {
					surface: 'login_modal',
				});
				setVisible(false);
			} else if (result?.isCancelled) {
				trackEvent('auth_cancelled', {
					auth_provider: 'facebook',
					surface: 'login_modal',
				});
			}
		} catch (error) {
			console.error('Facebook login error:', error);
			trackEvent('auth_attempt_failed', {
				auth_provider: 'facebook',
				...sanitizeTelemetryError(error, 'facebook_login_failed'),
			});
		} finally {
			setIsSocialLoading(false);
		}
	};

	const handlePhoneContinue = () => {
		trackEvent('phone_verification_requested', {
			surface: 'login_modal',
			has_phone_number: Boolean(phoneNumber.trim()),
			country_code: countryCode,
		});
		onContinue(phoneNumber);
	};

	const handleGoogleClick = () => {
		trackEvent('auth_method_clicked', {
			auth_provider: 'google',
			surface: 'login_modal',
		});
		googleLogin();
	};

	// No need for DOM-based triggers; use hook directly

	return (
		<>
			<Modal
				visible={visible}
				transparent={true}
				animationType="fade"
				onRequestClose={handleClose}
			>
				<View style={styles.overlay}>
					<View style={styles.modal}>
						{/* Header */}
						<View style={styles.header}>
							<Pressable style={styles.closeButton} onPress={handleClose}>
								<MaterialCommunityIcons name={"close" as any} size={24} color="#222" />
							</Pressable>
							<Text style={styles.headerTitle}>Log in or sign up</Text>
						</View>

						{/* Content */}
						<ScrollView
							style={styles.scrollView}
							contentContainerStyle={styles.content}
							keyboardShouldPersistTaps="handled"
							showsVerticalScrollIndicator={true}
						>
							<Text style={styles.welcomeTitle}>Welcome to Snap Camp</Text>

							{/* Country Code Dropdown */}
							<View style={styles.inputContainer}>
								<Text style={styles.inputLabel}>Country code</Text>
								<Pressable
									style={styles.dropdown}
									onPress={() => setIsCountryDropdownVisible(!isCountryDropdownVisible)}
								>
									<Text style={styles.dropdownText}>{countryCode}</Text>
									<MaterialCommunityIcons name={"chevron-down" as any} size={20} color="#222" />
								</Pressable>

							</View>

							{/* Phone Number Input */}
							<View style={styles.inputContainer}>
								<Text style={styles.inputLabel}>Phone number</Text>
								<TextInput
									style={[
										styles.textInput,
										phoneError ? styles.textInputError : null
									]}
									value={phoneNumber}
									onChangeText={(text) => {
										setPhoneNumber(text);
									}}
									placeholder="e.g., 5551234567"
									keyboardType="phone-pad"
									maxLength={15}
								/>
								{phoneError ? (
									<Text style={styles.errorText}>{phoneError}</Text>
								) : null}

								{/* Test number button for development */}
								{__DEV__ && (
									<Pressable
										style={styles.testButton}
										onPress={() => {
											setPhoneNumber('6506808824');
										}}
									>
										<Text style={styles.testButtonText}>Use Test Number</Text>
									</Pressable>
								)}
							</View>

							<Text style={styles.disclaimer}>
								We'll call or text you to confirm your number. Standard message and data rates apply.{' '}
								<Text style={styles.link}>Privacy Policy</Text>
							</Text>


							{/* Continue Button */}
							<Pressable
								style={[styles.continueButton, isAnyLoginLoading && styles.continueButtonDisabled]}
								onPress={() => !isAnyLoginLoading && handlePhoneContinue()}
								disabled={isAnyLoginLoading}
							>
								<Text style={styles.continueButtonText}>
									{isAnyLoginLoading ? 'Verifying account...' : 'Continue'}
								</Text>
							</Pressable>


							{/* Divider */}
							<View style={styles.divider}>
								<View style={styles.dividerLine} />
								<Text style={styles.dividerText}>or</Text>
								<View style={styles.dividerLine} />
							</View>

							{/* Social Login Options */}
							<View style={[styles.socialButtons, isAnyLoginLoading && styles.disabledContainer]}>
								{/* Google Sign-In */}
								<Pressable
									style={[
										styles.socialButton,
										(hoveredButton === 'google' || isAnyLoginLoading) && styles.socialButtonHovered,
										isAnyLoginLoading && styles.socialButtonDisabled
									]}
									onPress={() => !isAnyLoginLoading && handleGoogleClick()}
									onPressIn={() => !isAnyLoginLoading && setHoveredButton('google')}
									onPressOut={() => setHoveredButton(null)}
									onHoverIn={() => !isAnyLoginLoading && setHoveredButton('google')}
									onHoverOut={() => setHoveredButton(null)}
									disabled={isAnyLoginLoading}
								>
									<MaterialCommunityIcons name={"google" as any} size={20} color={isAnyLoginLoading ? "#999" : "#4285F4"} />
									<Text style={[styles.socialButtonText, isAnyLoginLoading && styles.disabledText]}>
										{isAnyLoginLoading ? 'Verifying account...' : 'Continue with Google'}
									</Text>
								</Pressable>

								{/* Facebook Sign-In with custom styling */}
								<Pressable
									style={[
										styles.socialButton,
										(hoveredButton === 'facebook' || isAnyLoginLoading) && styles.socialButtonHovered,
										isAnyLoginLoading && styles.socialButtonDisabled
									]}
									onPress={() => !isAnyLoginLoading && handleFacebookLogin()}
									onPressIn={() => !isAnyLoginLoading && setHoveredButton('facebook')}
									onPressOut={() => setHoveredButton(null)}
									onHoverIn={() => !isAnyLoginLoading && setHoveredButton('facebook')}
									onHoverOut={() => setHoveredButton(null)}
									disabled={isAnyLoginLoading}
								>
									<MaterialCommunityIcons name={"facebook" as any} size={20} color={isAnyLoginLoading ? "#999" : "#1877F2"} />
									<Text style={[styles.socialButtonText, isAnyLoginLoading && styles.disabledText]}>
										{isAnyLoginLoading ? 'Verifying account...' : 'Continue with Facebook'}
									</Text>
								</Pressable>
							</View>
						</ScrollView>
					</View>
				</View>
			</Modal>

			<Modal
				visible={isCountryDropdownVisible}
				transparent={true}
				animationType="fade"
				onRequestClose={() => setIsCountryDropdownVisible(false)}
			>
				<Pressable style={styles.countryModalOverlay} onPress={() => setIsCountryDropdownVisible(false)}>
					<Pressable style={styles.countryModalCard} onPress={() => {}}>
						<Text style={styles.countryModalTitle}>Select country</Text>
						<ScrollView style={styles.countryModalList}>
							{COUNTRIES.map((country) => (
								<Pressable
									key={`${country.name}-${country.code}`}
									style={({ pressed }) => [
										styles.countryItem,
										pressed && styles.countryItemPressed,
									]}
									onPress={() => {
										setCountryCode(`${country.name} (${country.code})`);
										setIsCountryDropdownVisible(false);
									}}
								>
									<Text style={styles.countryItemText}>
										{country.flag} {country.name} ({country.code})
									</Text>
								</Pressable>
							))}
						</ScrollView>
					</Pressable>
				</Pressable>
			</Modal>

			{newUserData && (
				<SignUpWizard
					visible={showAccountTypeSetup}
					onClose={handleSetupClose}
					phoneNumber=""
					firebaseUid={newUserData.firebaseUid}
					entrySource="google"
					email={newUserData.email}
					firstName={newUserData.firstName}
					lastName={newUserData.lastName}
					userName={newUserData.userName}
				/>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	modal: {
		backgroundColor: 'white',
		borderRadius: 12,
		width: '90%',
		maxWidth: 568,
		maxHeight: '90%',
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: 24,
		paddingVertical: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#EBEBEB',
		position: 'relative',
	},
	closeButton: {
		position: 'absolute',
		left: 24,
		zIndex: 1,
	},
	headerTitle: {
		flex: 1,
		textAlign: 'center',
		fontSize: 16,
		fontWeight: '600',
		color: '#222',
	},
	scrollView: {
		flexGrow: 0,
	},
	content: {
		padding: 24,
		flexGrow: 1,
	},
	welcomeTitle: {
		fontSize: 22,
		fontWeight: '600',
		color: '#222',
		marginBottom: 24,
	},
	inputContainer: {
		marginBottom: 16,
	},
	inputLabel: {
		fontSize: 12,
		color: '#717171',
		marginBottom: 8,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	dropdown: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderWidth: 1,
		borderColor: '#B0B0B0',
		borderRadius: 8,
		paddingHorizontal: 12,
		paddingVertical: 14,
		backgroundColor: 'white',
		zIndex: 2,
	},
	countryDropdownList: {
		position: 'absolute',
		top: '100%',
		left: 0,
		right: 0,
		backgroundColor: 'white',
		borderWidth: 1,
		borderColor: '#B0B0B0',
		borderRadius: 8,
		marginTop: 4,
		zIndex: 1000,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 4,
		elevation: 5,
	},
	countryModalOverlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.35)',
		justifyContent: 'center',
		alignItems: 'center',
		paddingHorizontal: 24,
	},
	countryModalCard: {
		width: '100%',
		maxWidth: 420,
		maxHeight: 340,
		backgroundColor: 'white',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#E5E7EB',
		paddingVertical: 8,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.18,
		shadowRadius: 8,
		elevation: 8,
	},
	countryModalTitle: {
		fontSize: 14,
		fontWeight: '600',
		color: '#222',
		paddingHorizontal: 16,
		paddingBottom: 10,
		paddingTop: 6,
		borderBottomWidth: 1,
		borderBottomColor: '#F3F4F6',
	},
	countryModalList: {
		maxHeight: 260,
	},
	countryItem: {
		paddingHorizontal: 16,
		paddingVertical: 12,
		borderBottomWidth: 1,
		borderBottomColor: '#F5F5F5',
	},
	countryItemPressed: {
		backgroundColor: '#F5F5F5',
	},
	countryItemText: {
		fontSize: 14,
		color: '#222',
	},
	dropdownText: {
		fontSize: 16,
		color: '#222',
	},
	textInput: {
		borderWidth: 1,
		borderColor: '#B0B0B0',
		borderRadius: 8,
		paddingHorizontal: 12,
		paddingVertical: 14,
		fontSize: 16,
		color: '#222',
		backgroundColor: 'white',
	},
	disclaimer: {
		fontSize: 12,
		color: '#717171',
		lineHeight: 16,
		marginBottom: 16,
	},
	link: {
		textDecorationLine: 'underline',
	},
	continueButton: {
		backgroundColor: '#3C8248',
		borderRadius: 8,
		paddingVertical: 14,
		alignItems: 'center',
		marginBottom: 16,
	},
	continueButtonText: {
		color: 'white',
		fontSize: 16,
		fontWeight: '600',
	},
	divider: {
		flexDirection: 'row',
		alignItems: 'center',
		marginVertical: 16,
	},
	dividerLine: {
		flex: 1,
		height: 1,
		backgroundColor: '#EBEBEB',
	},
	dividerText: {
		marginHorizontal: 16,
		fontSize: 12,
		color: '#717171',
	},
	socialButtons: {
		gap: 12,
	},
	socialButtonContainer: {
		marginBottom: 12,
	},
	socialButton: {
		flexDirection: 'row',
		alignItems: 'center',
		borderWidth: 1,
		borderColor: '#222',
		borderRadius: 8,
		paddingVertical: 14,
		paddingHorizontal: 16,
		backgroundColor: 'white',
		marginBottom: 12,
		position: 'relative',
	},
	socialButtonHovered: {
		backgroundColor: '#f8f9fa',
		borderColor: '#495057',
	},
	googleIcon: {
		width: 20,
		height: 20,
		borderRadius: 2,
		backgroundColor: 'white',
		alignItems: 'center',
		justifyContent: 'center',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.2,
		shadowRadius: 2,
		elevation: 2,
	},
	googleG: {
		fontSize: 14,
		fontWeight: 'bold',
		color: '#4285f4',
		fontFamily: 'Product Sans, Arial, sans-serif',
	},
	socialButtonText: {
		flex: 1,
		textAlign: 'center',
		fontSize: 14,
		fontWeight: '600',
		color: '#222',
		marginLeft: 0, // Reduced offset for better centering
	},
	verificationHint: {
		fontSize: 12,
		color: '#666',
		marginTop: 4,
		textAlign: 'center',
	},
	continueButtonDisabled: {
		backgroundColor: '#ccc',
		opacity: 0.6,
	},
	backButton: {
		marginTop: 12,
		paddingVertical: 8,
		alignItems: 'center',
	},
	backButtonText: {
		fontSize: 14,
		color: '#3C8248',
		fontWeight: '500',
	},
	recaptchaContainer: {
		alignItems: 'center',
		marginVertical: 16,
		minHeight: 78, // Standard reCAPTCHA height
		padding: 12,
		backgroundColor: '#f8f9fa',
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#e9ecef',
	},
	recaptchaText: {
		fontSize: 14,
		color: '#666',
		marginBottom: 12,
		textAlign: 'center',
	},
	textInputError: {
		borderColor: '#dc3545',
		borderWidth: 2,
	},
	errorText: {
		fontSize: 12,
		color: '#dc3545',
		marginTop: 4,
	},
	disabledContainer: {
		opacity: 0.8,
	},
	socialButtonDisabled: {
		borderColor: '#ddd',
		backgroundColor: '#f9f9f9',
	},
	disabledText: {
		color: '#999',
	},
	testButton: {
		marginTop: 8,
		paddingVertical: 6,
		paddingHorizontal: 12,
		backgroundColor: '#e9ecef',
		borderRadius: 4,
		alignSelf: 'flex-start',
	},
	testButtonText: {
		fontSize: 12,
		color: '#6c757d',
	},
});

export default LogInModal; 